import { type ContentNode, ContentNodeUtils, type QueryNode } from '@helo/cms'
import { If, Value, pipe } from '@helo/utils'

import type { QuerySlice } from '../../store/appStore/queryStore/querySlice'
import { Options } from '../Options'
import { RenderPipeline } from '../RenderPipeline'
import type { QueryInput } from './Query'
import { queryOptions } from './Query.options'

type QueryLookupCtx = {
  input: QueryInput
  output: {
    store: QuerySlice
    queryId?: string
    queryNode?: QueryNode
    isQueryLoading?: boolean
    isQueryError?: boolean
    queryError?: Error
    contentNode?: ContentNode | null
    isEmpty?: boolean | null
  }
}

/** Resolve query id and locator from defaults. */
const setupInput = () =>
  RenderPipeline.updateInput(input =>
    pipe(
      input,
      Options.applyDefaults(queryOptions),
      Options.validate(queryOptions),
    ),
  )

const setQueryLookup = () =>
  RenderPipeline.updateOutput((output: QueryLookupCtx['output'], ctx) => {
    const queryNode = output.store.queryStore.getQuery(ctx.input.queryId)
    const queryError = queryNode?.apolloState.error
    return {
      queryId: ctx.input.queryId,
      queryNode,
      isQueryLoading: queryNode?.apolloState.loading ?? true,
      isQueryError: queryError != null,
      queryError,
    }
  })

const setContentNode = () =>
  RenderPipeline.updateOutput((output: QueryLookupCtx['output'], ctx) => ({
    contentNode:
      ctx.input.contentNodeLocator && output.queryNode
        ? pipe(
            output.queryNode.queryContentNode,
            ContentNodeUtils.findContentNode(ctx.input.contentNodeLocator),
          )
        : null,
  }))

const setIsEmpty = () =>
  RenderPipeline.updateOutput((output: QueryLookupCtx['output']) => ({
    isEmpty: pipe(
      output.contentNode,
      ContentNodeUtils.getData(),
      If.isElse(
        Value.isArray,
        data => data.length === 0,
        () => null,
      ),
    ),
  }))

export const QueryInputParts = {
  setupInput,
}

export const QueryOutputParts = {
  setQueryLookup,
  setContentNode,
  setIsEmpty,
}
